import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  Home as AddressIcon,
  Description as DocumentIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { kycService } from '../services/kycService';
import { KYCSubmission, KYCSubmissionData, KYCDocument } from '../types/kyc';
import KYCStatusBadge from '../components/kyc/KYCStatusBadge';
import KYCDocumentUpload from '../components/kyc/KYCDocumentUpload';
import { useThemeMode } from '../contexts/ThemeContext';

// Country list for nationality/country selection
const countries = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France',
  'Japan', 'South Korea', 'Singapore', 'Switzerland', 'Netherlands', 'Sweden',
  'Norway', 'Denmark', 'Finland', 'Ireland', 'New Zealand', 'Austria', 'Belgium',
  'Spain', 'Italy', 'Portugal', 'Brazil', 'Mexico', 'India', 'Other',
];

const steps = [
  { label: 'Personal Information', icon: PersonIcon, description: 'Your basic details' },
  { label: 'Address', icon: AddressIcon, description: 'Your residential address' },
  { label: 'Documents', icon: DocumentIcon, description: 'Upload required documents' },
];

interface KYCFormData {
  full_name: string;
  date_of_birth: string;
  nationality: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
}

const KYC = () => {
  useTheme(); // for theme context
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [kycSubmission, setKycSubmission] = useState<KYCSubmission | null>(null);
  const [documents, setDocuments] = useState<KYCDocument[]>([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
  } = useForm<KYCFormData>({
    mode: 'onChange',
    defaultValues: {
      full_name: '',
      date_of_birth: '',
      nationality: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state_province: '',
      postal_code: '',
      country: '',
    },
  });

  // Fetch current KYC status
  useEffect(() => {
    const fetchKYCStatus = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const submission = await kycService.getKYCStatus(user.id);
        setKycSubmission(submission);

        if (submission) {
          // Fetch documents
          const docs = await kycService.getDocuments(user.id);
          setDocuments(docs);

          // Pre-fill form if submission exists
          reset({
            full_name: submission.full_name || '',
            date_of_birth: submission.date_of_birth || '',
            nationality: submission.nationality || '',
            address_line1: submission.address?.address_line1 || '',
            address_line2: submission.address?.address_line2 || '',
            city: submission.address?.city || '',
            state_province: submission.address?.state_province || '',
            postal_code: submission.address?.postal_code || '',
            country: submission.address?.country || '',
          });
        }
      } catch (err) {
        console.error('Failed to fetch KYC status:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchKYCStatus();
  }, [user?.id, reset]);

  const handleNext = async () => {
    if (activeStep === steps.length - 1) return;

    let isValid = false;

    if (activeStep === 0) {
      isValid = await trigger(['full_name', 'date_of_birth', 'nationality']);
    } else if (activeStep === 1) {
      isValid = await trigger(['address_line1', 'city', 'state_province', 'postal_code', 'country']);
    } else {
      isValid = true;
    }

    if (isValid) {
      setActiveStep((prev) => prev + 1);
      setError(null);
    } else {
      setError('Please fill in all required fields correctly');
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleDocumentUploaded = (document: KYCDocument) => {
    setDocuments((prev) => {
      const filtered = prev.filter((d) => d.document_type !== document.document_type);
      return [...filtered, document];
    });
  };

  const handleDocumentRemoved = (documentType: string) => {
    setDocuments((prev) => prev.filter((d) => d.document_type !== documentType));
  };

  const onSubmit = async (data: KYCFormData) => {
    if (!user?.id) {
      setError('Please log in to submit KYC');
      return;
    }

    // Check if we're on the documents step and have all required documents
    if (activeStep !== steps.length - 1) {
      setError('Please complete all steps');
      return;
    }

    const hasIdDocument = documents.some((d) => d.document_type === 'id_document');
    const hasProofOfAddress = documents.some((d) => d.document_type === 'proof_of_address');
    const hasSelfie = documents.some((d) => d.document_type === 'selfie');

    if (!hasIdDocument || !hasProofOfAddress || !hasSelfie) {
      setError('Please upload all required documents (ID, Proof of Address, and Selfie)');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const submissionData: KYCSubmissionData = {
        full_name: data.full_name,
        date_of_birth: data.date_of_birth,
        nationality: data.nationality,
        address: {
          address_line1: data.address_line1,
          address_line2: data.address_line2 || undefined,
          city: data.city,
          state_province: data.state_province,
          postal_code: data.postal_code,
          country: data.country,
        },
      };

      const submission = await kycService.submitKYC(user.id, submissionData);
      setKycSubmission(submission);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to submit KYC application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubmit = () => {
    setKycSubmission(null);
    setDocuments([]);
    setActiveStep(0);
    setSuccess(false);
    reset();
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', py: 4, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 3 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
        </Container>
      </Box>
    );
  }

  // KYC already approved
  if (kycSubmission?.status === 'approved') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          py: 4,
          background: isDark
            ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(34, 197, 94, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card
              sx={{
                background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${isDark ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.5)'}`,
                borderRadius: 3,
                textAlign: 'center',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #22C55E, #10B981)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                KYC Verified
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your identity has been verified. You can now access all features of the platform.
              </Typography>
              <KYCStatusBadge status="approved" size="medium" />
              <Box sx={{ mt: 4 }}>
                <Button variant="outlined" onClick={() => navigate('/profile')}>
                  Back to Profile
                </Button>
              </Box>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // KYC pending or under review
  if (kycSubmission?.status === 'pending' || kycSubmission?.status === 'under_review') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          py: 4,
          background: isDark
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(14, 165, 233, 0.03) 100%)',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card
              sx={{
                background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                borderRadius: 3,
                textAlign: 'center',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3B82F6, #0EA5E9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CircularProgress size={40} sx={{ color: 'white' }} />
              </Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                KYC Under Review
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your KYC application has been submitted and is currently being reviewed.
                We'll notify you once the verification is complete.
              </Typography>
              <KYCStatusBadge status={kycSubmission.status} size="medium" />

              {/* Submission details */}
              <Box sx={{ mt: 4, textAlign: 'left' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Submission Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Full Name</Typography>
                    <Typography variant="body1">{kycSubmission.full_name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Nationality</Typography>
                    <Typography variant="body1">{kycSubmission.nationality}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Submitted</Typography>
                    <Typography variant="body1">
                      {new Date(kycSubmission.submitted_at).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Documents</Typography>
                    <Typography variant="body1">{documents.length} uploaded</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mt: 4 }}>
                <Button variant="outlined" onClick={() => navigate('/profile')}>
                  Back to Profile
                </Button>
              </Box>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // KYC rejected - show reason and allow resubmission
  if (kycSubmission?.status === 'rejected') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          py: 4,
          background: isDark
            ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.03) 0%, rgba(220, 38, 38, 0.03) 100%)',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card
              sx={{
                background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.5)'}`,
                borderRadius: 3,
                textAlign: 'center',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <WarningIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                KYC Rejected
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Unfortunately, your KYC application was not approved.
              </Typography>
              <KYCStatusBadge status="rejected" size="medium" />

              {kycSubmission.rejection_reason && (
                <Alert severity="error" sx={{ mt: 3, textAlign: 'left' }}>
                  <Typography variant="subtitle2" fontWeight={600}>Reason:</Typography>
                  <Typography variant="body2">{kycSubmission.rejection_reason}</Typography>
                </Alert>
              )}

              {kycSubmission.notes && (
                <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                  <Typography variant="subtitle2" fontWeight={600}>Additional Notes:</Typography>
                  <Typography variant="body2">{kycSubmission.notes}</Typography>
                </Alert>
              )}

              <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="outlined" onClick={() => navigate('/profile')}>
                  Back to Profile
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleResubmit}
                  sx={{
                    background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1E3A8A, #2563EB)',
                    },
                  }}
                >
                  Resubmit KYC
                </Button>
              </Box>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // Success state after submission
  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          py: 4,
          background: isDark
            ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(34, 197, 94, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              sx={{
                background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${isDark ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.5)'}`,
                borderRadius: 3,
                textAlign: 'center',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #22C55E, #10B981)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                KYC Submitted Successfully
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your KYC application has been submitted and is now pending review.
                We'll notify you once the verification is complete.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/profile')}
                sx={{
                  background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1E3A8A, #2563EB)',
                  },
                }}
              >
                Back to Profile
              </Button>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // KYC form - no submission yet
  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(30, 64, 175, 0.03) 0%, rgba(14, 165, 233, 0.03) 100%)',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              KYC Verification
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete your identity verification to access all platform features
            </Typography>
          </Box>

          {/* Stepper */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2}>
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = index === activeStep;
                const isCompleted = index < activeStep;

                return (
                  <Grid item xs={12} md={4} key={step.label}>
                    <Card
                      sx={{
                        background: isActive
                          ? isDark
                            ? 'rgba(59, 130, 246, 0.1)'
                            : 'rgba(59, 130, 246, 0.05)'
                          : isDark
                          ? 'rgba(17, 24, 39, 0.9)'
                          : 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${
                          isActive
                            ? '#3B82F6'
                            : isCompleted
                            ? isDark
                              ? 'rgba(34, 197, 94, 0.3)'
                              : 'rgba(34, 197, 94, 0.5)'
                            : isDark
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.08)'
                        }`,
                        borderRadius: 2,
                        p: 2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isCompleted
                              ? 'linear-gradient(135deg, #22C55E, #10B981)'
                              : isActive
                              ? 'linear-gradient(135deg, #1E40AF, #3B82F6)'
                              : isDark
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.08)',
                            color: isCompleted || isActive ? 'white' : 'text.secondary',
                            mr: 2,
                          }}
                        >
                          {isCompleted ? <CheckIcon /> : <StepIcon />}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {step.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {step.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Form Card */}
          <Card
            sx={{
              background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Step 0: Personal Information */}
                {activeStep === 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      Personal Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Please provide your personal details as they appear on your ID document
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Controller
                          name="full_name"
                          control={control}
                          rules={{ required: 'Full name is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Full Name"
                              placeholder="Enter your full legal name"
                              error={!!errors.full_name}
                              helperText={errors.full_name?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="date_of_birth"
                          control={control}
                          rules={{ required: 'Date of birth is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Date of Birth"
                              type="date"
                              InputLabelProps={{ shrink: true }}
                              error={!!errors.date_of_birth}
                              helperText={errors.date_of_birth?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="nationality"
                          control={control}
                          rules={{ required: 'Nationality is required' }}
                          render={({ field }) => (
                            <FormControl fullWidth error={!!errors.nationality}>
                              <InputLabel>Nationality</InputLabel>
                              <Select {...field} label="Nationality">
                                {countries.map((country) => (
                                  <MenuItem key={country} value={country}>
                                    {country}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>
                    </Grid>
                  </motion.div>
                )}

                {/* Step 1: Address */}
                {activeStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      Residential Address
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Please provide your current residential address
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Controller
                          name="address_line1"
                          control={control}
                          rules={{ required: 'Address is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Address Line 1"
                              placeholder="Street address"
                              error={!!errors.address_line1}
                              helperText={errors.address_line1?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          name="address_line2"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Address Line 2 (Optional)"
                              placeholder="Apartment, suite, unit, etc."
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="city"
                          control={control}
                          rules={{ required: 'City is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="City"
                              error={!!errors.city}
                              helperText={errors.city?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="state_province"
                          control={control}
                          rules={{ required: 'State/Province is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="State / Province"
                              error={!!errors.state_province}
                              helperText={errors.state_province?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="postal_code"
                          control={control}
                          rules={{ required: 'Postal code is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Postal Code"
                              error={!!errors.postal_code}
                              helperText={errors.postal_code?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="country"
                          control={control}
                          rules={{ required: 'Country is required' }}
                          render={({ field }) => (
                            <FormControl fullWidth error={!!errors.country}>
                              <InputLabel>Country</InputLabel>
                              <Select {...field} label="Country">
                                {countries.map((country) => (
                                  <MenuItem key={country} value={country}>
                                    {country}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>
                    </Grid>
                  </motion.div>
                )}

                {/* Step 2: Documents */}
                {activeStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      Document Upload
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Please upload the following documents for identity verification
                    </Typography>

                    <KYCDocumentUpload
                      userId={user?.id || ''}
                      existingDocuments={documents}
                      onDocumentUploaded={handleDocumentUploaded}
                      onDocumentRemoved={handleDocumentRemoved}
                      disabled={submitting}
                    />

                    <Alert severity="info" sx={{ mt: 3 }}>
                      <Typography variant="body2">
                        All three documents are required: ID Document, Proof of Address, and Selfie with ID.
                        Supported formats: PDF, JPG, PNG (max 10MB each).
                      </Typography>
                    </Alert>
                  </motion.div>
                )}

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    disabled={activeStep === 0 || submitting}
                  >
                    Back
                  </Button>

                  {activeStep < steps.length - 1 ? (
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={handleNext}
                      sx={{
                        background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1E3A8A, #2563EB)',
                        },
                      }}
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      disabled={submitting}
                      sx={{
                        background: 'linear-gradient(135deg, #22C55E, #10B981)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #16A34A, #059669)',
                        },
                      }}
                    >
                      {submitting ? 'Submitting...' : 'Submit KYC'}
                    </Button>
                  )}
                </Box>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default KYC;
